import { useReducer, useCallback } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import routes from "./apiClient.routes";
import common from "../config/config.common";

const initialState = {
  loading: false,
  errors: null,
  data: null,
  requestId: null,
  requestExtra: null,
};

const defaultHeaders = {
  "Content-Type": "application/json",
  "Cache-Control": "no-cache",
  "Access-Control-Allow-Origin": "*",
};

const fromRouteParams = (url, routeParams) => {
  if (!routeParams) return url;

  let treatedUrl = url;
  Object.keys(routeParams).forEach((key) => {
    treatedUrl = treatedUrl.replace(`:${key}`, routeParams[key]);
  });
  return treatedUrl;
};

const transformApiErrors = (error) => {
  /**
   * O apiClient garante a propriedade 'messsage' em todos o cenários.
   *
   * A API implementa 3 padrões para apresentação de erros:
   * - string no body do response:
   *      Ex.: { message: '' }
   * - string com erros separados por ||:
   *      Ex.: { message: '{primeiro_erro}', errors: [ 0: 'msg1', 1: 'msg2' ] }
   * - string com erros separados por || e nome do campo antes da mensagem:
   *      Ex.: { message: '{primeiro_erro}', errors: [ field1: 'msg1', field2: 'msg2' ] }
   *
   *  Nota: é possível ter um objeto com os dois últimos cenários:
   *      Ex.: { message: '{primeiro_erro}', errors: [ '0': 'msgA', '1': 'msgB', field1: 'msg1', field2: 'msg2' ] }
   * */

  const errors = {};
  const defaultMessage = { message: "Desculpe, ocorreu um erro durante o processamento da requisição. " };

  try {
    if (!error || !error.response || !error.response.data) {
      return defaultMessage;
    }

    let { data } = error.response;

    if (typeof data === "object" && data.result) data = data.result;

    if (typeof data === "string") {
      const splitted = data.split("||");

      let msgWithoutFieldIndex = 0;
      for (let i = 0; i < splitted.length; i += 1) {
        const msg = splitted[i];

        // Se a mensagem tem um formato válido para extração do nome do campo
        if (msg.split(":").length === 2 && msg.split(":")[0].indexOf(" ") === -1) {
          const [key, value] = msg.split(":");
          errors[key] = value;
          if (!errors.message) errors.message = "Existem erros na validação dos dados";
        } else if (!errors.message) {
          errors.message = msg;
        } else {
          errors[`${msgWithoutFieldIndex}`] = msg;
          msgWithoutFieldIndex += 1;
        }
      }
    }
  } catch (e) {
    console.error("Falha no tratamento do response", e);
    return defaultMessage;
  }
  return errors;
};

const apiClientReducer = (currentState, action) => {
  switch (action.type) {
    case "SEND":
      return {
        loading: true,
        errors: null,
        data: null,
        requestId: action.requestId,
        requestExtra: null,
      };
    case "RESPONSE":
      return {
        ...currentState,
        loading: false,
        data: action.responseData,
        requestExtra: action.requestExtra,
        requestId: action.requestId,
      };
    case "ERROR":
      return {
        loading: false,
        errors: action.errors,
        requestExtra: action.requestExtra,
        requestId: action.requestId,
      };
    case "CLEAR":
      return initialState;
    default:
      throw new Error("Desculpe, alguma coisa deu errado!");
  }
};

const useApiClient = (handleSecurityIssue = true) => {
  const [httpState, dispatch] = useReducer(apiClientReducer, initialState);
  const history = useHistory();
  const { replace } = history;

  const clear = useCallback(() => dispatch({ type: "CLEAR" }), []);

  const request = useCallback(
    (apiRoute, params, { headers, routeParams, requestId, requestExtra } = {}) => {
      dispatch({ type: "SEND", requestId });

      const successFn = (response) => {
        dispatch({
          type: "RESPONSE",
          responseData: response.data || {},
          requestExtra,
          requestId,
        });
      };

      const failureFn = (error) => {
        let withMessages = { message: "" };
        if (error.response) {
          /*
           * Qualquer status code diferente de 2xx;
           */
          if (error.response.status === 401 && handleSecurityIssue) {
            const toastId = "6f3a3954-1909-4f43-9bfe-0c1b31155ba7";
            toast.error("Seu tempo de sessão expirou. Por favor, autentique-se novamente. ", { toastId });
            replace("/");
            withMessages = null;
          }

          if (error.response.status === 403 && handleSecurityIssue) {
            replace("/app/403");
            withMessages = null;
          }

          withMessages = transformApiErrors(error);
        } else if (error.request) {
          /**
           * Erro de comunicação com o servidor.
           * A requisição foi realizada, mas sem resposta.
           */
          withMessages = { message: "Ocorreu um erro de comunicação com o servidor. " };
        } else {
          // Something happened in setting up the request and triggered an Error
          withMessages = { message: "Ocorreu um erro ao preparar essa requisição. " };
        }

        dispatch({
          type: "ERROR",
          errors: withMessages,
          requestExtra,
          requestId,
        });
      };

      const authHeaders = { client_id: common.clientId };
      const storedToken = localStorage.getItem("authToken");

      if (storedToken) {
        const token = JSON.parse(storedToken);
        authHeaders.Authorization = `Bearer ${token.id_token}`;
      }

      axios
        .request({
          url: fromRouteParams(apiRoute.url, routeParams),
          method: apiRoute.method,
          params: apiRoute.method === "GET" ? params : null,
          data: apiRoute.method === "POST" ? params : null,
          withCredentials: true,
          headers: {
            ...defaultHeaders,
            ...authHeaders,
            ...headers,
          },
          responseType: apiRoute.responseType,
        })
        .then(successFn)
        .catch(failureFn);
    },
    [handleSecurityIssue, replace],
  );

  return {
    request,
    clear,
    isLoading: httpState.loading,
    data: httpState.data,
    errors: httpState.errors,
    requestId: httpState.requestId,
    requestExtra: httpState.requestExtra,
  };
};

export const apiRoutes = routes;

export default useApiClient;
